/**
 * <product-reviews-carousel>
 * Customer Favorites carousel. One instance per element (supports multiple per page).
 * Accessibility: pause/resume control, honors prefers-reduced-motion, pauses on
 * hover/focus, hides off-screen slides from the tab order, announces slide changes
 * politely only while auto-rotation is stopped (WAI-ARIA carousel pattern).
 */
if (!customElements.get('product-reviews-carousel')) {
  class ProductReviewsCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-carousel-track]');
      this.slides = Array.from(this.querySelectorAll('[data-carousel-slide]'));
      this.dots = Array.from(this.querySelectorAll('.product-reviews-carousel__dot'));
      this.pauseButton = this.querySelector('[data-carousel-pause]');
      this.liveRegion = this.querySelector('[data-carousel-live]');
      this.counter = this.querySelector('[data-carousel-counter]');
      if (!this.track || this.slides.length === 0) return;

      this.index = 0;
      this.timer = null;
      this.userPaused = false;
      this.hovered = false;
      this.desktopQuery = window.matchMedia('(min-width: 768px)');
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.autoplayEnabled =
        this.dataset.autoplay === 'true' && this.slides.length > 1 && !this.reducedMotion.matches;
      this.speed = (parseInt(this.dataset.autoplaySpeed, 10) || 6) * 1000;

      this.querySelector('[data-carousel-prev]')?.addEventListener('click', () => this.step(-1));
      this.querySelector('[data-carousel-next]')?.addEventListener('click', () => this.step(1));
      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => this.goTo(parseInt(dot.dataset.slide, 10)));
      });

      if (this.pauseButton) {
        if (this.autoplayEnabled) {
          this.pauseButton.hidden = false;
          this.pauseButton.addEventListener('click', () => this.togglePause());
        } else {
          this.pauseButton.hidden = true;
        }
      }

      this.addEventListener('mouseenter', () => { this.hovered = true; this.syncAutoplay(); });
      this.addEventListener('mouseleave', () => { this.hovered = false; this.syncAutoplay(); });
      this.addEventListener('focusin', () => this.syncAutoplay());
      this.addEventListener('focusout', () => this.syncAutoplay());
      this.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') { event.preventDefault(); this.step(-1); }
        if (event.key === 'ArrowRight') { event.preventDefault(); this.step(1); }
      });

      this.onViewportChange = () => this.update(false);
      this.desktopQuery.addEventListener('change', this.onViewportChange);
      this.onVisibilityChange = () => this.syncAutoplay();
      document.addEventListener('visibilitychange', this.onVisibilityChange);

      this.update(false);
      this.syncAutoplay();
    }

    disconnectedCallback() {
      this.stopTimer();
      this.desktopQuery?.removeEventListener('change', this.onViewportChange);
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
    }

    get perView() {
      return this.desktopQuery.matches ? 3 : 1;
    }

    get maxIndex() {
      return Math.max(0, this.slides.length - this.perView);
    }

    step(direction) {
      const count = this.maxIndex + 1;
      this.goTo((this.index + direction + count) % count);
    }

    goTo(index) {
      this.index = Math.min(Math.max(index, 0), this.maxIndex);
      this.update(true);
    }

    update(announce) {
      this.index = Math.min(this.index, this.maxIndex);
      this.track.style.transform = `translateX(-${this.index * (100 / this.perView)}%)`;

      this.slides.forEach((slide, i) => {
        const visible = i >= this.index && i < this.index + this.perView;
        slide.toggleAttribute('inert', !visible);
        slide.setAttribute('aria-hidden', visible ? 'false' : 'true');
      });

      this.dots.forEach((dot, i) => {
        dot.hidden = i > this.maxIndex;
        dot.classList.toggle('active', i === this.index);
        if (i === this.index) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });

      if (this.counter) {
        this.counter.textContent = `${this.index + 1} / ${this.maxIndex + 1}`;
      }

      if (announce && this.liveRegion) {
        this.liveRegion.textContent = `Review ${this.index + 1} of ${this.maxIndex + 1}`;
      }
    }

    togglePause() {
      this.userPaused = !this.userPaused;
      this.pauseButton.toggleAttribute('data-paused', this.userPaused);
      this.pauseButton.setAttribute(
        'aria-label',
        this.userPaused ? 'Resume automatic rotation' : 'Pause automatic rotation'
      );
      this.syncAutoplay();
    }

    syncAutoplay() {
      const shouldRun =
        this.autoplayEnabled &&
        !this.userPaused &&
        !this.hovered &&
        !this.contains(document.activeElement) &&
        !document.hidden;

      if (this.liveRegion) {
        this.liveRegion.setAttribute('aria-live', shouldRun ? 'off' : 'polite');
      }
      if (shouldRun && !this.timer) {
        this.timer = setInterval(() => this.step(1), this.speed);
      } else if (!shouldRun) {
        this.stopTimer();
      }
    }

    stopTimer() {
      if (this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }
    }
  }

  customElements.define('product-reviews-carousel', ProductReviewsCarousel);
}
